import {
	useCallback,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import {
	MoreHorizontal,
	Send,
	Zap,
	Smile,
	Paperclip,
	X,
	Reply,
	ShieldOff,
	UserX,
	Gift,
} from 'lucide-react'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { Textarea } from '@/common/ui/textarea'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'

import type { components } from '@/schema'
import type { LucideIcon } from 'lucide-react'

type ChatMessageResponse = components['schemas']['ChatMessageResponse']
type ChatInputAction = {
	key: string
	label: string
	icon: LucideIcon
	onSelect: () => void
	isActive: boolean
}

const COMPACT_ACTIONS_WIDTH = 360
const COMPACT_ACTIONS_WITH_CREDIT_WIDTH = 400

interface ChatInputProps {
	input: string
	onInputChange: (value: string) => void
	onSend: () => void
	onKeyDown: (e: React.KeyboardEvent) => void
	showQuickResponses: boolean
	showStickerPicker: boolean
	showMediaPicker: boolean
	onToggleQuickResponses: () => void
	onToggleStickerPicker: () => void
	onToggleMediaPicker: () => void
	onToggleInstantCreditClaim?: () => void
	replyToMessage?: ChatMessageResponse | null
	onCancelReply?: () => void
	isBanned?: boolean
	onUnban?: () => void
	isClosed?: boolean
	onPasteFile?: (file: File) => void
}

function getReplyPreview(message: ChatMessageResponse): string {
	switch (message.messageType) {
		case 'TEXT':
			return message.text?.slice(0, 50) || ''
		case 'STICKER':
			return '[สติกเกอร์]'
		case 'IMAGE':
			return '[รูปภาพ]'
		case 'VIDEO':
			return '[วิดีโอ]'
		case 'AUDIO':
			return '[เสียง]'
		case 'FILE':
			return `[ไฟล์] ${message.fileName || ''}`
		case 'LOCATION':
			return '[ตำแหน่ง]'
		default:
			return '[ข้อความ]'
	}
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
	function ChatInput(
		{
			input,
			onInputChange,
			onSend,
			onKeyDown,
			showQuickResponses,
			showStickerPicker,
			showMediaPicker,
			onToggleQuickResponses,
			onToggleStickerPicker,
			onToggleMediaPicker,
			onToggleInstantCreditClaim,
			replyToMessage,
			onCancelReply,
			isBanned,
			onUnban,
			isClosed,
			onPasteFile,
		},
		ref,
	) {
		const textareaRef = useRef<HTMLTextAreaElement>(null)
		const inputRowRef = useRef<HTMLDivElement>(null)
		const [isActionMenuCompact, setIsActionMenuCompact] = useState(false)

		// Forward ref to parent
		useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement)

		// Auto-resize textarea based on content
		useEffect(() => {
			const textarea = textareaRef.current
			if (!textarea) return

			// Reset height to calculate correct scrollHeight
			textarea.style.height = '0px'
			// Set height to scrollHeight, with min 40px and max 120px
			const scrollHeight = textarea.scrollHeight
			const newHeight = Math.max(40, Math.min(scrollHeight, 120))
			textarea.style.height = `${newHeight}px`
			// Enable scroll only when content exceeds max height
			textarea.style.overflowY = scrollHeight > 120 ? 'auto' : 'hidden'
		}, [input])

		const handlePaste = useCallback(
			(e: React.ClipboardEvent) => {
				if (!onPasteFile) return

				const items = e.clipboardData?.items
				if (!items) return

				for (const item of items) {
					// Check if the item is a file (image, video, etc.)
					if (item.kind === 'file') {
						const file = item.getAsFile()
						if (file) {
							e.preventDefault()
							onPasteFile(file)
							return
						}
					}
				}
			},
			[onPasteFile],
		)

		useEffect(() => {
			const inputRow = inputRowRef.current
			if (!inputRow) return

			const compactWidth = onToggleInstantCreditClaim
				? COMPACT_ACTIONS_WITH_CREDIT_WIDTH
				: COMPACT_ACTIONS_WIDTH

			const updateCompactMode = (width: number) => {
				setIsActionMenuCompact(width < compactWidth)
			}

			updateCompactMode(inputRow.clientWidth)

			if (typeof ResizeObserver === 'undefined') {
				return
			}

			const resizeObserver = new ResizeObserver(([entry]) => {
				updateCompactMode(entry?.contentRect.width ?? inputRow.clientWidth)
			})

			resizeObserver.observe(inputRow)

			return () => resizeObserver.disconnect()
		}, [onToggleInstantCreditClaim])

		const chatInputActions: ChatInputAction[] = [
			{
				key: 'quick-responses',
				label: 'ข้อความด่วน',
				icon: Zap,
				onSelect: onToggleQuickResponses,
				isActive: showQuickResponses,
			},
			{
				key: 'stickers',
				label: 'สติกเกอร์',
				icon: Smile,
				onSelect: onToggleStickerPicker,
				isActive: showStickerPicker,
			},
			{
				key: 'media',
				label: 'ส่งไฟล์แนบ',
				icon: Paperclip,
				onSelect: onToggleMediaPicker,
				isActive: showMediaPicker,
			},
		]

		if (onToggleInstantCreditClaim) {
			chatInputActions.push({
				key: 'instant-credit-claim',
				label: 'ส่งลิงก์รับเครดิตฟรี',
				icon: Gift,
				onSelect: onToggleInstantCreditClaim,
				isActive: false,
			})
		}

		// Show closed UI when room is closed (user unfollowed)
		if (isClosed) {
			return (
				<div className="border-t">
					<div className="flex items-center justify-center gap-3 p-4">
						<UserX className="h-5 w-5 text-muted-foreground" />
						<span className="text-sm text-muted-foreground">
							ผู้ใช้ยกเลิกการติดตามแล้ว ไม่สามารถส่งข้อความได้
						</span>
					</div>
				</div>
			)
		}

		// Show unban UI when room is banned
		if (isBanned) {
			return (
				<div className="border-t">
					<div className="flex items-center justify-center gap-3 p-4">
						<span className="text-sm text-muted-foreground">
							ห้องแชทนี้ถูกระงับ
						</span>
						{onUnban && (
							<Button
								variant="destructive"
								size="sm"
								onClick={onUnban}
								className="gap-1"
							>
								<ShieldOff className="h-4 w-4" />
								ยกเลิกการระงับ
							</Button>
						)}
					</div>
				</div>
			)
		}

		return (
			<div className="border-t">
				{/* Reply Preview */}
				{replyToMessage && (
					<div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2">
						<Reply className="h-4 w-4 text-muted-foreground" />
						<div className="min-w-0 flex-1">
							<span className="text-xs font-medium text-muted-foreground">
								ตอบกลับ{' '}
								{replyToMessage.direction === 'INCOMING'
									? 'ลูกค้า'
									: replyToMessage.sentByOperator?.username || 'คุณ'}
							</span>
							<p className="truncate text-sm">
								{getReplyPreview(replyToMessage)}
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 shrink-0"
							onClick={onCancelReply}
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
				)}

				<div
					ref={inputRowRef}
					className="flex items-end gap-1.5 p-2 md:gap-2 md:p-4"
				>
					<div className="flex shrink-0">
						{isActionMenuCompact ? (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className={cn(
											'h-8 w-8 md:h-10 md:w-10',
											chatInputActions.some((action) => action.isActive) &&
												'bg-primary/10 text-primary',
										)}
										aria-label="เปิดเมนูเครื่องมือแชท"
									>
										<MoreHorizontal className="h-4 w-4 md:h-5 md:w-5" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" side="top">
									{chatInputActions.map((action) => {
										const Icon = action.icon

										return (
											<DropdownMenuItem
												key={action.key}
												onClick={action.onSelect}
												className={cn(
													'gap-2',
													action.isActive && 'bg-primary/10 text-primary',
												)}
											>
												<Icon className="h-4 w-4" />
												{action.label}
											</DropdownMenuItem>
										)
									})}
								</DropdownMenuContent>
							</DropdownMenu>
						) : (
							<TooltipProvider>
								{chatInputActions.map((action) => {
									const Icon = action.icon

									return (
										<Tooltip key={action.key}>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													onClick={action.onSelect}
													className={cn(
														'h-8 w-8 md:h-10 md:w-10',
														action.isActive && 'bg-primary/10 text-primary',
													)}
													aria-label={action.label}
												>
													<Icon className="h-4 w-4 md:h-5 md:w-5" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>{action.label}</TooltipContent>
										</Tooltip>
									)
								})}
							</TooltipProvider>
						)}
					</div>

					<Textarea
						ref={textareaRef}
						value={input}
						onChange={(e) => onInputChange(e.target.value)}
						onKeyDown={onKeyDown}
						onPaste={handlePaste}
						placeholder="พิมพ์ข้อความ..."
						rows={1}
						className={cn(
							'!min-h-0 flex-1 resize-none py-2 text-base focus-visible:ring-0',
						)}
					/>

					<Button
						onClick={onSend}
						disabled={!input.trim()}
						size="icon"
						className="h-8 w-8 shrink-0 md:h-10 md:w-10"
					>
						<Send className="h-4 w-4 md:h-5 md:w-5" />
					</Button>
				</div>
			</div>
		)
	},
)
